import renderBreadcrumbItem from "../../features/navigation/renderBreadcrumbItem.tsx";
import { t, useStoreLanguage } from "../../features/store-language/i18n.ts";
import React, { useMemo } from "react";
import { Breadcrumb as AntBreadcrumb } from "antd";
import DirectionalIcon from "../../features/navigation/DirectionalIcon.tsx";
import { useLocation, useNavigate } from "react-router-dom";
import "./ShopBreadcrumb.css";

type ShopBreadcrumbItem = {
  title?: React.ReactNode;
  href?: string;
  type?: string;
};

type ShopBreadcrumbProps = {
  items?: ShopBreadcrumbItem[];
  actions?: React.ReactNode;
  context?: React.ReactNode;
  hierarchy?: boolean;
  navigation?: React.ReactNode;
  title?: React.ReactNode;
  className?: string;
  fallbackPath?: string;
  variant?: "order";
  onBack?: () => void;
  backLabel?: string;
};

function normalizeBreadcrumbItems(items?: ShopBreadcrumbItem[]) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter(function (item) {
    return item && item.type !== "separator" && item.title != null;
  });
}

function ShopBreadcrumb(props: ShopBreadcrumbProps) {
  useStoreLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const fallbackPath = props.fallbackPath || "/";
  const normalizedItems = useMemo(
    function () {
      return normalizeBreadcrumbItems(props.items);
    },
    [props.items],
  );
  const pageTitle = props.title ?? normalizedItems[0]?.title;
  const trailItems = props.title ? normalizedItems : normalizedItems.slice(1);
  const hierarchyItems = [...trailItems].reverse().concat(pageTitle != null ? [{
    title: (
      <bdi
        aria-current="page"
        title={typeof pageTitle === "string" ? pageTitle : undefined}
      >
        {pageTitle}
      </bdi>
    ),
  }] : []);
  const pageActions = props.actions;
  const hasActions = pageActions != null && pageActions !== false && pageActions !== "";
  const hasNavigation = props.navigation != null && props.navigation !== false && props.navigation !== "";
  const rootClassName = [
    "shop-breadcrumb",
    props.variant === "order" ? "shop-breadcrumb--order" : "",
    props.hierarchy ? "shop-breadcrumb--hierarchy" : "",
    hasActions ? "shop-breadcrumb--with-actions" : "",
    hasNavigation ? "shop-breadcrumb--with-navigation" : "",
    props.className,
  ]
    .filter(Boolean)
    .join(" ");

  function handleBack() {
    if (props.onBack) {
      props.onBack();
      return;
    }
    const historyIndex = window.history.state?.idx;
    if (typeof historyIndex === "number" && historyIndex > 0) {
      navigate(-1);
      return;
    }

    if (location.key !== "default") {
      navigate(-1);
      return;
    }

    navigate(fallbackPath);
  }

  const actionContent = hasActions ? (
    <div
      className="shop-breadcrumb__actions"
      role="group"
      aria-label={t("عملیات صفحه")}
    >
      {pageActions}
    </div>
  ) : null;
  return (
    <div
      className={rootClassName}
    >
      <div className="shop-breadcrumb__inner">
        <button
          type="button"
          className="shop-breadcrumb__back"
          onClick={handleBack}
          aria-label={t("بازگشت")}
        >
          <DirectionalIcon
            direction="back"
            size={18}
            weight="bold"
          />
          <span>{props.backLabel || t("بازگشت")}</span>
        </button>
        {props.context ? (
          <div
            className="shop-breadcrumb__actions shop-breadcrumb__context"
            role="group"
            aria-label={t("سفارش جاری")}
          >
            {props.context}
          </div>
        ) : null}
        <div className="shop-breadcrumb__main">
          {actionContent}
          <nav
            className="shop-breadcrumb__trail"
            aria-label={t("مسیر صفحه")}
          >
            {!props.hierarchy && pageTitle != null ? (
              <span className="shop-breadcrumb__page-title">{pageTitle}</span>
            ) : null}
            {!props.hierarchy && pageTitle != null && trailItems.length > 0 ? (
              <span className="shop-breadcrumb__title-separator" aria-hidden="true">
                :
              </span>
            ) : null}
            {(props.hierarchy ? hierarchyItems.length : trailItems.length) > 0 ? (
              <AntBreadcrumb
                itemRender={renderBreadcrumbItem}
                separator={
                  <span className="shop-breadcrumb__path-separator" aria-hidden="true">
                    <DirectionalIcon
                      direction="forward"
                      variant="chevron"
                      size={12}
                    />
                  </span>
                }
                items={props.hierarchy ? hierarchyItems : trailItems}
              />
            ) : null}
          </nav>
        </div>
      </div>
      {hasNavigation ? (
        <div className="shop-breadcrumb__navigation">
          {props.navigation}
        </div>
      ) : null}
    </div>
  );
}

export default ShopBreadcrumb;
